import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCustomerKey,
  ShopifyGiftCardKey,
  ShopifyGiftCardToCustomerKey,
  ShopifyGiftCardToOrderKey,
  ShopifyGiftCardToShopKey,
  ShopifyOrderKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";
import { ShopifyApiVersion } from "./ShopifyApiVersion";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyGiftCardKey,
  "2022-01": {
    name: "Shopify Gift Card",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Balance",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "balance",
            graphQLSelection: `amount currencyCode`,
          },
        },
      },
      {
        name: "Initial Value",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "initialValue", graphQLSelection: `amount currencyCode` } },
      },
      {
        name: "Last Characters",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "lastCharacters" } },
      },
      {
        name: "Masked Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "maskedCode" } },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "note" } },
      },
      {
        name: "Expires On",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "expiresOn" } },
        configuration: {
          includeTime: false,
        },
      },
      {
        name: "Enabled",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "enabled" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt" } },
      },
      {
        name: "Disabled At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "disabledAt" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyGiftCardToOrderKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "order", graphQLSelection: "id" },
        },
      },
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifyGiftCardToCustomerKey,
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "customer", graphQLSelection: "id" },
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyGiftCardToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "giftCards",
    },
  },
  "2024-10": {
    fields: [
      {
        name: "Disabled At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "disabledAt" } },
        isDeleted: true,
      },
      {
        name: "Deactivated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "deactivatedAt" } },
      },
    ],
  },
};

export const ShopifyGiftCard = ({ apiVersion }: { apiVersion: ShopifyApiVersion }) => buildConnectionSpec(specs, apiVersion);
